import { z } from 'zod';
import { CountryCodeSchema } from './geo';

/* ------------------------------------------------------------------ */
/* Public read API (W21): /v1/public/*                                 */
/*                                                                     */
/* What the SEO site (capsicoeats.com) renders: live towns, live       */
/* restaurants and their menus. No sign-in, cached, rate limited, and  */
/* never any personal data (no phones, no owner names, no customers).  */
/* ------------------------------------------------------------------ */

/** Restaurants per page on a town page. */
export const PUBLIC_PAGE_SIZE = 24;

/** Outlet slugs: name-locality, lowercase ASCII words joined by "-". */
export const PublicSlugSchema = z
  .string()
  .trim()
  .min(1)
  .max(90)
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Invalid slug');

export const PublicTownSlugSchema = z
  .string()
  .trim()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/i, 'Invalid town');

export const PublicSlugParamsSchema = z.object({ slug: PublicSlugSchema });
export const PublicTownParamsSchema = z.object({ slug: PublicTownSlugSchema });

export const PublicHygieneBadgeSchema = z.enum(['green', 'amber']);
export type PublicHygieneBadge = z.infer<typeof PublicHygieneBadgeSchema>;

export const PublicTownSchema = z.object({
  slug: z.string(),
  name: z.string(),
  country: CountryCodeSchema,
  region: z.string().nullable(),
  liveRestaurants: z.number().int(),
});
export type PublicTown = z.infer<typeof PublicTownSchema>;

export const PublicTownListSchema = z.object({ towns: z.array(PublicTownSchema) });
export type PublicTownList = z.infer<typeof PublicTownListSchema>;

export const PublicCuisineCountSchema = z.object({
  name: z.string(),
  restaurants: z.number().int(),
});
export type PublicCuisineCount = z.infer<typeof PublicCuisineCountSchema>;

export const PublicCollectionSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  subtitle: z.string().nullable(),
  /** Restaurant slugs of a hand-picked collection (rule-based collections list none). */
  restaurantSlugs: z.array(z.string()),
});
export type PublicCollection = z.infer<typeof PublicCollectionSchema>;

export const PublicServiceDaySchema = z.object({
  /** 0 = Sunday … 6 = Saturday. */
  weekday: z.number().int().min(0).max(6),
  closed: z.boolean(),
  /** Minutes after midnight, town local time. */
  opensMin: z.number().int(),
  closesMin: z.number().int(),
});
export type PublicServiceDay = z.infer<typeof PublicServiceDaySchema>;

export const PublicTownDetailSchema = PublicTownSchema.extend({
  timezone: z.string(),
  currency: z.string(),
  zones: z.array(z.object({ code: z.string(), name: z.string() })),
  topCuisines: z.array(PublicCuisineCountSchema),
  collections: z.array(PublicCollectionSchema),
  serviceHours: z.array(PublicServiceDaySchema),
});
export type PublicTownDetail = z.infer<typeof PublicTownDetailSchema>;

export const PublicRestaurantCardSchema = z.object({
  /** Outlet id, used by the app deep link capsico://restaurant/<id>. */
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  locality: z.string(),
  cuisines: z.array(z.string()),
  foodType: z.enum(['veg', 'non_veg', 'both']),
  costForTwoMinor: z.number().int().nullable(),
  rating: z.object({ average: z.number().nullable(), count: z.number().int() }),
  hygieneBadge: PublicHygieneBadgeSchema.nullable(),
  coverUrl: z.string().nullable(),
});
export type PublicRestaurantCard = z.infer<typeof PublicRestaurantCardSchema>;

export const PublicTownRestaurantsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(500).default(1),
  cuisine: z.string().trim().min(1).max(40).optional(),
});
export type PublicTownRestaurantsQuery = z.infer<typeof PublicTownRestaurantsQuerySchema>;

export const PublicRestaurantPageSchema = z.object({
  town: z.object({ slug: z.string(), name: z.string(), country: CountryCodeSchema }),
  cuisine: z.string().nullable(),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  restaurants: z.array(PublicRestaurantCardSchema),
});
export type PublicRestaurantPage = z.infer<typeof PublicRestaurantPageSchema>;

export const PublicDishSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  foodType: z.enum(['veg', 'non_veg', 'egg']),
  priceMinor: z.number().int(),
  isBestseller: z.boolean(),
  photoUrl: z.string().nullable(),
});
export type PublicDish = z.infer<typeof PublicDishSchema>;

export const PublicMenuCategorySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  dishes: z.array(PublicDishSchema),
});
export type PublicMenuCategory = z.infer<typeof PublicMenuCategorySchema>;

export const PublicRestaurantSchema = PublicRestaurantCardSchema.extend({
  restaurantName: z.string(),
  town: z.object({ slug: z.string(), name: z.string(), country: CountryCodeSchema }),
  currency: z.string(),
  timezone: z.string(),
  coverLargeUrl: z.string().nullable(),
  openingHours: z.array(PublicServiceDaySchema),
  menu: z.array(PublicMenuCategorySchema),
  updatedAt: z.string(),
});
export type PublicRestaurant = z.infer<typeof PublicRestaurantSchema>;

export const PublicSitemapQuerySchema = z.object({
  part: z.coerce.number().int().min(0).max(1000).default(0),
});
export type PublicSitemapQuery = z.infer<typeof PublicSitemapQuerySchema>;

/** Rows per sitemap chunk (well under the 50,000 URL limit of one sitemap file). */
export const PUBLIC_SITEMAP_CHUNK = 5000;

export const PublicSitemapSchema = z.object({
  part: z.number().int(),
  parts: z.number().int(),
  towns: z.array(z.object({ slug: z.string(), country: CountryCodeSchema })),
  restaurants: z.array(
    z.object({
      slug: z.string(),
      townSlug: z.string(),
      country: CountryCodeSchema,
      updatedAt: z.string(),
    }),
  ),
});
export type PublicSitemap = z.infer<typeof PublicSitemapSchema>;
